import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useApp } from '../context/AppContext';
import { getAuthorizedWashers, getWashAssignments, saveWashAssignments } from '../services/api';
import { toLocalISODate } from '../utils/dateUtils';

export default function WashAssignments() {
  const { vehicles, drivers, canManageAssignments, isAdmin } = useApp();
  const [date, setDate] = useState(() => toLocalISODate());
  const [rows, setRows] = useState({});
  const [washers, setWashers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const vehicleList = useMemo(
    () => [...vehicles].sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [vehicles]
  );

  const washerOptions = useMemo(() => {
    const fromApi = Array.isArray(washers) ? washers : [];
    if (fromApi.length > 0) {
      return [...fromApi].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    }
    return [...drivers]
      .filter((d) => d.isWasher)
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [washers, drivers]);

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const [res, washerRes] = await Promise.all([
        getWashAssignments(date),
        getAuthorizedWashers().catch(() => ({ washers: [] })),
      ]);
      const list = Array.isArray(res?.assignments) ? res.assignments : [];
      const next = {};
      list.forEach((a) => {
        const vid = a.vehicle?._id || a.vehicle;
        if (vid) next[String(vid)] = String(a.driver?._id || a.driver || '');
      });
      setRows(next);
      const washerList = Array.isArray(washerRes?.washers)
        ? washerRes.washers
        : (Array.isArray(washerRes) ? washerRes : []);
      setWashers(washerList);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load wash assignments');
      setRows({});
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const setWasherForVehicle = (vehicleId, driverId) => {
    setRows((prev) => {
      const next = { ...prev };
      if (!driverId) {
        delete next[String(vehicleId)];
      } else {
        next[String(vehicleId)] = driverId;
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!canManageAssignments) {
      toast.error('Sign in as dispatcher or admin to save wash assignments.');
      return;
    }
    const assignments = Object.entries(rows)
      .filter(([, driverId]) => driverId)
      .map(([vehicle, driver]) => ({ vehicle, driver }));

    setSaving(true);
    try {
      await saveWashAssignments(date, assignments);
      toast.success(`Wash assignments saved for ${date}`);
      await loadAssignments();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save wash assignments');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <h2>Daily Wash Assignments</h2>
        <p>
          Assign which rider washes each vehicle for the selected day. Washer names then show on
          Vehicle Wash for that same date. Guards can still mark washed without an assignment.
          {canManageAssignments ? '' : ' View only — sign in as dispatcher or admin to edit.'}
        </p>
      </div>

      <div className="card" style={{ maxWidth: 900 }}>
        <div className="form-grid cols2" style={{ marginBottom: 16 }}>
          <div className="form-group">
            <label>Assignment date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          {canManageAssignments && (
            <div className="form-group" style={{ alignSelf: 'end' }}>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || loading}
              >
                {saving ? 'Saving…' : 'Save wash assignments'}
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <p className="text-muted">Loading wash assignments…</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Plate</th>
                  <th>Assigned washer (rider)</th>
                </tr>
              </thead>
              <tbody>
                {vehicleList.map((v) => {
                  const vid = String(v._id);
                  return (
                    <tr key={v._id}>
                      <td>{v.name}</td>
                      <td className="mono text-muted">{v.plate}</td>
                      <td>
                        <select
                          value={rows[vid] || ''}
                          onChange={(e) => setWasherForVehicle(v._id, e.target.value)}
                          disabled={!canManageAssignments}
                        >
                          <option value="">— Not assigned —</option>
                          {washerOptions.map((d) => (
                            <option key={d._id} value={d._id}>{d.name}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!canManageAssignments && (
          <p className="text-muted text-sm" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => window.location.assign('/login')}
            >
              Sign in
            </button>
            {' '}as dispatcher or admin to edit.
          </p>
        )}

        {isAdmin && (
          <p className="text-muted text-sm" style={{ marginTop: 8 }}>
            Tip: open <strong>Vehicle Wash</strong> after saving so the assigned rider can tick washed.
          </p>
        )}
      </div>
    </>
  );
}
